import { Box, Link, Typography } from '@mui/material'
import { formatApiDate } from '~/shared/lib/date'
import sortBy from 'lodash/sortBy'
import { FC, useState } from 'react'
import { CheckBox, Linkify, PriorityIcon } from '~/shared/ui'
import { TaskDueDate } from '~/entities/task'
import { AssigneeGroupListItem } from '~/entities/assignee-group'
import { CarePlanItem } from './carePlanTypes'
import type { Provider } from '~/entities/provider'
import type { PatientUser } from '~/shared/types'
import type { VariantType } from 'notistack'
import { useMutation } from '~/shared/hooks/useMutation'

// Task type extracted from CarePlanItem
type CarePlanTask = NonNullable<CarePlanItem['tasks']>[number]

interface CarePlanProps {
  carePlan: CarePlanItem
  patient: PatientUser
  providers: Provider[]
  assigneeGroups: AssigneeGroupListItem[]
  openModal: (carePlan: CarePlanItem | null, editClick?: boolean) => void
  toggleTaskComplete: (
    carePlanId: number,
    taskId: number,
    data: { is_complete: boolean },
  ) => Promise<void>
  onCareplanCreateOrUpdate: (carePlan: CarePlanItem) => void
  dispatchNotification: (notification: { variant?: VariantType; message: string }) => void
  setModalCarePlanAction: (
    data: {
      carePlan: CarePlanItem
      action: string
      onCarePlanActionChange: (action: string) => void
    } | null,
  ) => void
  hideShowCompletedToggle?: boolean
}

interface CarePlanTaskRowProps {
  task: CarePlanTask
  assigneeName: string
  createdByName: string
  onToggleComplete: (task: CarePlanTask) => Promise<void>
}

const CarePlanTaskRow: FC<CarePlanTaskRowProps> = ({
  task,
  assigneeName,
  createdByName,
  onToggleComplete,
}) => {
  const { due_date, title } = task
  // The row owns its toggle, so each task shows its own in-flight state and reports its own failure.
  const { run: toggleComplete, loading } = useMutation(() => onToggleComplete(task), {
    error: 'Failed to update the task',
  })

  return (
    <Box
      data-testid="care-plan-task"
      sx={[
        {
          position: 'relative',
          pl: '3.5rem',
          mb: '3rem',
          transition: '100ms linear opacity',
          '& button': { position: 'absolute', top: '.5rem', left: '0rem' },
        },
        loading && { pointerEvents: 'none', opacity: 0.5 },
      ]}
    >
      <CheckBox value={task.is_complete ?? false} onChecked={toggleComplete} disabled={loading} />
      <Box sx={{ mt: '-0.5em', wordBreak: 'break-word' }}>
        <Typography component="div">
          <Linkify>
            <Box sx={{ display: 'flex', pt: '0.3rem' }}>
              <Box sx={{ mr: '1rem' }}>
                {task?.priority !== undefined && task.priority !== 0 ? (
                  <PriorityIcon level={task.priority} variant="view" />
                ) : null}
              </Box>
              {title}
            </Box>
          </Linkify>
        </Typography>
        <Box sx={{ fontSize: '1.4rem' }}>
          <Box component="span" sx={{ fontWeight: 800, padding: '0 .5em' }}>
            Assigned to
          </Box>
          {assigneeName} |{' '}
          {due_date && <TaskDueDate dueDate={due_date} isComplete={task.is_complete ?? false} />}
          <Typography sx={{ fontStyle: 'italic', color: 'grey.600', fontSize: '1.25rem' }}>
            — Created by {createdByName} on
            {` ${formatApiDate(task.created_at, 'EEE, MMM d, yyyy h:mm a') ?? ''}`}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

const CarePlan: FC<CarePlanProps> = ({
  carePlan,
  patient,
  providers,
  assigneeGroups,
  toggleTaskComplete: toggleTaskCompleteProp,
  hideShowCompletedToggle,
}) => {
  const [showCompleted, setShowCompleted] = useState(false)

  const toggleComplete = (task: CarePlanTask) =>
    toggleTaskCompleteProp(carePlan.id, task.id, { is_complete: !task.is_complete })

  const renderTasks = (orderedTasks: CarePlanTask[]) =>
    orderedTasks.map((task, i) => {
      // Resolve `task.owner_group` to an assignee name. The redux `assigneeGroups`
      // list is fetched with `groups_only=true`, which excludes every user-personal
      // assignee group, so patient-owned and provider-owned tasks must be resolved
      // from `patient.assignee_group` and `providers[*].assignee_group` first.
      let assignee: AssigneeGroupListItem | { id: number; name: string } | Record<string, never> =
        {}
      const patientGroup = patient.assignee_group
      if (patientGroup && patientGroup.id === task.owner_group) {
        assignee = patientGroup
      }
      if (Object.keys(assignee).length === 0) {
        assignee =
          providers.find(user => user.assignee_group?.id === task.owner_group)?.assignee_group ?? {}
      }
      if (Object.keys(assignee).length === 0) {
        assignee = assigneeGroups.find(group => group.id === task.owner_group) ?? {}
      }
      const assigneeName = 'name' in assignee ? assignee.name : ''

      let createdByName: string
      const createdByUser = providers.find(user => user.id === task.created_by)
      if (createdByUser === undefined) {
        createdByName = 'N/A'
      } else {
        createdByName = `${createdByUser.first_name} ${createdByUser.last_name}`
      }

      return (
        <CarePlanTaskRow
          key={`task_${i}`}
          task={task}
          assigneeName={assigneeName}
          createdByName={createdByName}
          onToggleComplete={toggleComplete}
        />
      )
    })

  const renderShowCompletedLink = (completedTasksCount: number) => {
    return completedTasksCount > 0 ? (
      <Box sx={{ fontWeight: 'bold' }}>
        <Link
          onClick={() => setShowCompleted(!showCompleted)}
          color="secondary"
          sx={{ '&:hover': { cursor: 'pointer', color: 'primary.main' } }}
          underline="always"
        >
          {showCompleted
            ? `HIDE ${completedTasksCount} COMPLETED TASKS`
            : `SHOW ${completedTasksCount} COMPLETED TASKS`}
        </Link>
      </Box>
    ) : null
  }

  const { notes } = carePlan
  const allTasks = carePlan.tasks ?? []
  const completedTasksCount = allTasks.filter(task => task.is_complete).length
  const tasks =
    showCompleted || hideShowCompletedToggle ? allTasks : allTasks.filter(task => !task.is_complete)
  const orderedTasks = sortBy(tasks, ['is_complete', 'due_date'])

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        py: 1,
        mx: 2,
        borderBottom: '0.1rem solid',
        borderBottomColor: 'grey.300',
      }}
    >
      <Box sx={{ flex: 1, px: 1 }}>
        <Typography sx={{ my: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          <Linkify>{notes}</Linkify>
        </Typography>
        <Typography
          sx={{ textTransform: 'uppercase', fontSize: '1.4rem', mb: '1rem', fontWeight: 800 }}
        >
          tasks
        </Typography>
        {renderTasks(orderedTasks)}
        {!hideShowCompletedToggle ? renderShowCompletedLink(completedTasksCount) : null}
      </Box>
    </Box>
  )
}

export default CarePlan
